// Utility functions to convert between ProfileFields and .ini format

export interface BarcodeProfileFields {
  rasterDPI?: string;
  barcodeReRasterDpi?: string;

  // Linear
  Linear_IgnoreBarCode: boolean;
  Linear_ScanGapHor: string;
  Linear_ScanGapVer: string;
  Linear_MinScore: string;
  Linear_MinPharmacodeWidth: string;
  Linear_MinPharmacodeHeight: string;
  Linear_MinPharmacodeBars: string;
  Linear_MinVicinityHor: string;
  Linear_MinVicinityVer: string;
  Linear_EdgeThresh: string;
  Linear_MaxPharmacodeWidth: string;
  Linear_MaxPharmacodeHeight: string;
  Linear_ISO_IEC_15416_2016: boolean;
  Linear_EnableRotation: boolean;
  Linear_BackgroundColor: boolean;
  Linear_IgnoreChecksum: boolean;
  Linear_GradeDataStructure: boolean;
  Linear_GradeQuietZone: boolean;
  Linear_GradeNarrowBar: boolean;
  Linear_GradeWideToNarrowRatio: boolean;
  Linear_FailOutOfRangeBWR: boolean;
  Linear_MinBWR: string;
  Linear_MaxBWR: string;
  Linear_UspsMeasurementNominal: boolean;
  Linear_HumanReadable: boolean;

  // Linear Types
  IgnoreBarCodeType_UPCA: boolean;
  IgnoreBarCodeType_UPCE: boolean;
  IgnoreBarCodeType_EAN2: boolean;
  IgnoreBarCodeType_EAN5: boolean;
  IgnoreBarCodeType_EAN8: boolean;
  IgnoreBarCodeType_EAN13: boolean;
  IgnoreBarCodeType_I25: boolean;
  IgnoreBarCodeType_DATABAR: boolean;
  IgnoreBarCodeType_DATABAR_EXP: boolean;
  IgnoreBarCodeType_DATABAR_LTD: boolean;
  IgnoreBarCodeType_CODABAR: boolean;
  IgnoreBarCodeType_CODE11: boolean;
  IgnoreBarCodeType_CODE32: boolean;
  IgnoreBarCodeType_CODE39: boolean;
  IgnoreBarCodeType_CODE93: boolean;
  IgnoreBarCodeType_CODE128: boolean;
  IgnoreBarCodeType_MSI: boolean;
  IgnoreBarCodeType_TELEPEN: boolean;
  IgnoreBarCodeType_PHARMACODE: boolean;
  IgnoreBarCodeType_USPS_4STATE: boolean;

  // DataMatrix
  DataMatrix_IgnoreDmtxCode: boolean;
  DataMatrix_MaxExpectedSymbols: string;
  DataMatrix_ScanGap: string;
  DataMatrix_LocatorEffortLevel: string;
  DataMatrix_EdgeThresh: string;
  DataMatrix_MinModuleSize: string;
  DataMatrix_MaxSymbolSize: string;
  DataMatrix_MaxSkewAngle: string;
  DataMatrix_GradeNonISOParams: boolean;
  DataMatrix_EnableMirrored: boolean;

  // QR
  QR_IgnoreQrCode: boolean;
  QR_ScanGapHor: string;
  QR_ScanGapVer: string;
  QR_MinScore: string;
  QR_EdgeThresh: string;
  QR_GradeNonISOParams: boolean;
  QR_EnableMirrored: boolean;
  QR_ResolvedUrl: boolean;

  // Options
  General_RedLightSimulation: boolean;
  General_SmoothFilter: string;
  General_MedianFilter: string;
  General_OpenFilter: string;
  General_CloseFilter: string;
  General_OutputFormatGS1: string;
  General_OutputFormatGrade: string;
}

export const defaultBarcodeFields: BarcodeProfileFields = {
  rasterDPI: undefined,
  barcodeReRasterDpi: undefined,
  Linear_IgnoreBarCode: false,
  Linear_ScanGapHor: '2',
  Linear_ScanGapVer: '2',
  Linear_MinScore: '5',
  Linear_MinPharmacodeWidth: '20',
  Linear_MinPharmacodeHeight: '20',
  Linear_MinPharmacodeBars: '4',
  Linear_MinVicinityHor: '64',
  Linear_MinVicinityVer: '76',
  Linear_EdgeThresh: '40',
  Linear_MaxPharmacodeWidth: '600',
  Linear_MaxPharmacodeHeight: '200',
  Linear_ISO_IEC_15416_2016: true,
  Linear_EnableRotation: true,
  Linear_BackgroundColor: false,
  Linear_IgnoreChecksum: false,
  Linear_GradeDataStructure: false,
  Linear_GradeQuietZone: false,
  Linear_GradeNarrowBar: false,
  Linear_GradeWideToNarrowRatio: false,
  Linear_FailOutOfRangeBWR: false,
  Linear_MinBWR: '-0.1',
  Linear_MaxBWR: '0.1',
  Linear_UspsMeasurementNominal: true,
  Linear_HumanReadable: false,
  IgnoreBarCodeType_UPCA: false,
  IgnoreBarCodeType_UPCE: false,
  IgnoreBarCodeType_EAN2: false,
  IgnoreBarCodeType_EAN5: false,
  IgnoreBarCodeType_EAN8: false,
  IgnoreBarCodeType_EAN13: false,
  IgnoreBarCodeType_I25: false,
  IgnoreBarCodeType_DATABAR: false,
  IgnoreBarCodeType_DATABAR_EXP: false,
  IgnoreBarCodeType_DATABAR_LTD: false,
  IgnoreBarCodeType_CODABAR: false,
  IgnoreBarCodeType_CODE11: false,
  IgnoreBarCodeType_CODE32: true,
  IgnoreBarCodeType_CODE39: false,
  IgnoreBarCodeType_CODE93: false,
  IgnoreBarCodeType_CODE128: false,
  IgnoreBarCodeType_MSI: false,
  IgnoreBarCodeType_TELEPEN: false,
  IgnoreBarCodeType_PHARMACODE: false,
  IgnoreBarCodeType_USPS_4STATE: false,
  DataMatrix_IgnoreDmtxCode: false,
  DataMatrix_MaxExpectedSymbols: '3',
  DataMatrix_ScanGap: '8',
  DataMatrix_LocatorEffortLevel: '1',
  DataMatrix_EdgeThresh: '10',
  DataMatrix_MinModuleSize: '4',
  DataMatrix_MaxSymbolSize: '600',
  DataMatrix_MaxSkewAngle: '15',
  DataMatrix_GradeNonISOParams: false,
  DataMatrix_EnableMirrored: true,
  QR_IgnoreQrCode: false,
  QR_ScanGapHor: '1',
  QR_ScanGapVer: '1',
  QR_MinScore: '5',
  QR_EdgeThresh: '40',
  QR_GradeNonISOParams: false,
  QR_EnableMirrored: true,
  QR_ResolvedUrl: false,
  General_RedLightSimulation: true,
  General_SmoothFilter: '0',
  General_MedianFilter: '0',
  General_OpenFilter: '0',
  General_CloseFilter: '0',
  General_OutputFormatGS1: '0',
  General_OutputFormatGrade: '0',
};

export interface ProfileFields {
  // Parameters Section
  DiffGrouping: string;
  CheckContentText: boolean;
  CheckArtWorkText: boolean;
  CheckContent: boolean;
  CheckArtwork: boolean;
  MatchPer: string;
  MatchVar: string;
  CleanEdgePixel: string;
  MaskDielinePixel: string;
  AutoDistort: boolean;
  PaddSample: boolean;

  // ContentText Section (Low Contrast Text)
  ContentText_MinDiffDia: string;
  ContentText_MaxDiffShift: string;
  ContentText_Sensitivity: string;
  ContentText_Smoothing: string;
  ContentText_Refine: string;

  // ArtworkText Section (High Contrast Text)
  ArtworkText_MinDiffDia: string;
  ArtworkText_MaxDiffShift: string;
  ArtworkText_Sensitivity: string;
  ArtworkText_Smoothing: string;
  ArtworkText_Refine: string;

  // Content Section (Low Contrast Artwork)
  Content_MinDiffDia: string;
  Content_MaxDiffShift: string;
  Content_Sensitivity: string;
  Content_Smoothing: string;
  Content_Refine: string;

  // Artwork Section (High Contrast Artwork)
  Artwork_MinDiffDia: string;
  Artwork_MaxDiffShift: string;
  Artwork_Sensitivity: string;
  Artwork_Smoothing: string;
  Artwork_Refine: string;

  // Color Section
  Color_Enabled: boolean;
  Color_Min: string;
  Color_Mean: string;
}

export const defaultGraphicFields: ProfileFields = {
  DiffGrouping: '50',
  CheckContentText: true,
  CheckArtWorkText: true,
  CheckContent: true,
  CheckArtwork: true,
  MatchPer: '35',
  MatchVar: '85',
  CleanEdgePixel: '0',
  MaskDielinePixel: '0',
  AutoDistort: false,
  PaddSample: false,
  ContentText_MinDiffDia: '1',
  ContentText_MaxDiffShift: '0',
  ContentText_Sensitivity: '25',
  ContentText_Smoothing: '1',
  ContentText_Refine: '0',
  ArtworkText_MinDiffDia: '1',
  ArtworkText_MaxDiffShift: '0',
  ArtworkText_Sensitivity: '160',
  ArtworkText_Smoothing: '5',
  ArtworkText_Refine: '5',
  Content_MinDiffDia: '10',
  Content_MaxDiffShift: '0',
  Content_Sensitivity: '40',
  Content_Smoothing: '2',
  Content_Refine: '2',
  Artwork_MinDiffDia: '10',
  Artwork_MaxDiffShift: '0',
  Artwork_Sensitivity: '40',
  Artwork_Smoothing: '2',
  Artwork_Refine: '2',
  Color_Enabled: true,
  Color_Min: '1000',
  Color_Mean: '120',
};

const MAX_PROFILE_SIZE_BYTES = 100_000; // 100 KB

/**
 * Strips BOM, normalizes line endings, and removes non-printable control
 * characters from a raw profile string. Safe to call on both .ini and .xml.
 */
export function sanitizeProfileString(content: string): string {
  // Strip UTF-8 BOM
  let s = content.replace(/^\uFEFF/, '');
  // Normalize line endings to LF
  s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  // Remove null bytes and non-printable control chars (keep \t and \n)
  s = s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  return s;
}

/**
 * Returns an error message string if the content is not a valid graphic (.ini)
 * profile, or null if it passes all checks.
 */
export function validateGraphicProfileString(content: string): string | null {
  if (new Blob([content]).size > MAX_PROFILE_SIZE_BYTES) {
    return 'File is too large (max 100 KB).';
  }
  if (!/\[Parameters\]/i.test(content)) {
    return 'Invalid graphic profile: missing [Parameters] section.';
  }
  return null;
}

/**
 * Returns an error message string if the content is not a valid barcode (.xml)
 * profile, or null if it passes all checks.
 */
export function validateBarcodeProfileString(content: string): string | null {
  if (new Blob([content]).size > MAX_PROFILE_SIZE_BYTES) {
    return 'File is too large (max 100 KB).';
  }
  if (/<!DOCTYPE/i.test(content)) {
    return 'Invalid barcode profile: DOCTYPE declarations are not allowed.';
  }
  if (!/<BarcodeParams\b/.test(content)) {
    return 'Invalid barcode profile: missing <BarcodeParams> root element.';
  }
  return null;
}

/** Returns the keys of fields that differ between two profile field objects. */
export function getEditedFields(newFields: unknown, originalFields: unknown): string[] {
  const n = newFields as Record<string, unknown>;
  const o = originalFields as Record<string, unknown>;
  return Object.keys(n).filter((key) => String(n[key]) !== String(o[key]));
}

export const formatGraphicProfileToIni = (fields: ProfileFields): string => {
  const sections: string[] = [];

  // [Master] Section
  sections.push('[Master]');
  sections.push('ThreshA=0');
  sections.push('ThreshB=0');
  sections.push('');

  // [Sample] Section
  sections.push('[Sample]');
  sections.push('ThreshA=0');
  sections.push('ThreshB=0');
  sections.push('');

  // [Parameters] Section
  sections.push('[Parameters]');
  sections.push('EnhancedEdge=true');
  sections.push(`CheckArtWorkText=${fields.CheckArtWorkText}`);
  sections.push(`CheckContentText=${fields.CheckContentText}`);
  sections.push(`DiffGrouping=${fields.DiffGrouping.trim()}`);
  sections.push('SuggestedDPI=300');
  sections.push('MatchThresh=true');
  // Note: MatchVar stores 100 - UI value
  const matchVarValue = fields.MatchVar ? 100 - Number(fields.MatchVar.trim()) : 15;
  sections.push(`MatchVar=${matchVarValue}`);
  sections.push(`MatchPer=${fields.MatchPer.trim()}`);
  sections.push('CheckNested=true');
  sections.push(`CheckArtwork=${fields.CheckArtwork}`);
  sections.push(`CheckContent=${fields.CheckContent}`);
  sections.push('CheckBarcodes=false');
  sections.push(`PaddSample=${fields.PaddSample}`);
  sections.push('AutoDistortPadding=0');
  sections.push('AutoDistortVariance=10');
  sections.push(`CleanEdgePixel=${fields.CleanEdgePixel.trim()}`);
  sections.push(`MaskDielinePixel=${fields.MaskDielinePixel.trim()}`);
  sections.push(`AutoDistort=${fields.AutoDistort}`);
  sections.push('RepeatOverlapX=0.34999999999999998');
  sections.push('RepeatOverlapY=0.34999999999999998');
  sections.push('ExtractSeparations=false');
  sections.push('MinFontSize=1');
  sections.push('MaxFontSize=85');
  sections.push('');

  // [Color] Section
  sections.push('[Color]');
  sections.push(`Enabled=${fields.Color_Enabled}`);
  sections.push('Delta=5');
  // Note: Color Mean stores 225 - UI value
  const colorMeanValue = fields.Color_Mean ? 225 - Number(fields.Color_Mean.trim()) : 105;
  sections.push(`Mean=${colorMeanValue}`);
  sections.push(`Min=${fields.Color_Min.trim()}`);
  sections.push('Max=999999');
  sections.push('');

  // [Artwork] Section (High Contrast Artwork)
  sections.push('[Artwork]');
  sections.push('PreSmoothing=NONE');
  sections.push('SmoothingType=GAUSSIAN');
  sections.push(`Refine=${fields.Artwork_Refine.trim()}`);
  sections.push(`Sensitivity=${fields.Artwork_Sensitivity.trim()}`);
  sections.push(`MaxDiffShift=${fields.Artwork_MaxDiffShift.trim()}`);
  sections.push(`MinDiffDia=${fields.Artwork_MinDiffDia.trim()}`);
  sections.push('MaxDiffDia=0');
  sections.push(`Smoothing=${fields.Artwork_Smoothing.trim()}`);
  sections.push('');

  // [Content] Section (Low Contrast Artwork)
  sections.push('[Content]');
  sections.push('PreSmoothing=NONE');
  sections.push('SmoothingType=GAUSSIAN');
  sections.push(`Refine=${fields.Content_Refine.trim()}`);
  sections.push(`Sensitivity=${fields.Content_Sensitivity.trim()}`);
  sections.push(`MaxDiffShift=${fields.Content_MaxDiffShift.trim()}`);
  sections.push(`MinDiffDia=${fields.Content_MinDiffDia.trim()}`);
  sections.push('MaxDiffDia=0');
  sections.push(`Smoothing=${fields.Content_Smoothing.trim()}`);
  sections.push('');

  // [ArtworkText] Section (High Contrast Text)
  sections.push('[ArtworkText]');
  sections.push('PreSmoothing=BILATERAL');
  sections.push('SmoothingType=GAUSSIAN');
  sections.push(`Refine=${fields.ArtworkText_Refine.trim()}`);
  sections.push(`Sensitivity=${fields.ArtworkText_Sensitivity.trim()}`);
  sections.push(`MaxDiffShift=${fields.ArtworkText_MaxDiffShift.trim()}`);
  sections.push(`MinDiffDia=${fields.ArtworkText_MinDiffDia.trim()}`);
  sections.push('MaxDiffDia=0');
  sections.push(`Smoothing=${fields.ArtworkText_Smoothing.trim()}`);
  sections.push('');

  // [ContentText] Section (Low Contrast Text)
  sections.push('[ContentText]');
  sections.push('PreSmoothing=NONE');
  sections.push('SmoothingType=GAUSSIAN');
  sections.push(`Refine=${fields.ContentText_Refine.trim()}`);
  sections.push(`Sensitivity=${fields.ContentText_Sensitivity.trim()}`);
  sections.push(`MaxDiffShift=${fields.ContentText_MaxDiffShift.trim()}`);
  sections.push(`MinDiffDia=${fields.ContentText_MinDiffDia.trim()}`);
  sections.push('MaxDiffDia=0');
  sections.push(`Smoothing=${fields.ContentText_Smoothing.trim()}`);
  sections.push('');

  return sections.join('\n');
};

export const parseIniToProfileFields = (iniString: string): ProfileFields => {
  const lines = iniString.split('\n');
  const sections: { [key: string]: { [key: string]: string } } = {};
  let currentSection = '';

  // Parse .ini file into sections
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      currentSection = trimmed.slice(1, -1);
      sections[currentSection] = {};
    } else if (trimmed && trimmed.includes('=')) {
      const [key, ...valueParts] = trimmed.split('=');
      const value = valueParts.join('=');
      if (currentSection) {
        sections[currentSection][key] = value;
      }
    }
  });

  // Extract values from sections
  const params = sections['Parameters'] || {};
  const color = sections['Color'] || {};
  const artwork = sections['Artwork'] || {};
  const content = sections['Content'] || {};
  const artworkText = sections['ArtworkText'] || {};
  const contentText = sections['ContentText'] || {};

  // Note: MatchVar is stored as 100 - UI value
  const matchVar = params['MatchVar'] ? String(100 - Number(params['MatchVar'])) : '85';
  // Note: Color Mean is stored as 225 - UI value
  const colorMean = color['Mean'] ? String(225 - Number(color['Mean'])) : '120';

  return {
    DiffGrouping: params['DiffGrouping'] || '50',
    CheckContentText: params['CheckContentText'] === 'true',
    CheckArtWorkText: params['CheckArtWorkText'] === 'true',
    CheckContent: params['CheckContent'] === 'true',
    CheckArtwork: params['CheckArtwork'] === 'true',
    MatchPer: params['MatchPer'] || '35',
    MatchVar: matchVar,
    CleanEdgePixel: params['CleanEdgePixel'] || '0',
    MaskDielinePixel: params['MaskDielinePixel'] || '0',
    AutoDistort: params['AutoDistort'] === 'true',
    PaddSample: params['PaddSample'] === 'true',

    ContentText_MinDiffDia: contentText['MinDiffDia'] || '1',
    ContentText_MaxDiffShift: contentText['MaxDiffShift'] || '0',
    ContentText_Sensitivity: contentText['Sensitivity'] || '25',
    ContentText_Smoothing: contentText['Smoothing'] || '1',
    ContentText_Refine: contentText['Refine'] || '0',

    ArtworkText_MinDiffDia: artworkText['MinDiffDia'] || '1',
    ArtworkText_MaxDiffShift: artworkText['MaxDiffShift'] || '0',
    ArtworkText_Sensitivity: artworkText['Sensitivity'] || '160',
    ArtworkText_Smoothing: artworkText['Smoothing'] || '5',
    ArtworkText_Refine: artworkText['Refine'] || '5',

    Content_MinDiffDia: content['MinDiffDia'] || '10',
    Content_MaxDiffShift: content['MaxDiffShift'] || '0',
    Content_Sensitivity: content['Sensitivity'] || '40',
    Content_Smoothing: content['Smoothing'] || '2',
    Content_Refine: content['Refine'] || '2',

    Artwork_MinDiffDia: artwork['MinDiffDia'] || '10',
    Artwork_MaxDiffShift: artwork['MaxDiffShift'] || '0',
    Artwork_Sensitivity: artwork['Sensitivity'] || '40',
    Artwork_Smoothing: artwork['Smoothing'] || '2',
    Artwork_Refine: artwork['Refine'] || '2',

    Color_Enabled: color['Enabled'] === 'true',
    Color_Min: color['Min'] || '1000',
    Color_Mean: colorMean,
  };
};

// Barcode Profile Formatters (XML format!)
export const formatBarcodeProfileToIni = (fields: BarcodeProfileFields): string => {
  const lines: string[] = [];

  // Helper to capitalize boolean values for XML
  const boolToXml = (value: boolean) => (value ? 'True' : 'False');

  // Build IgnoreBarCodeType comma-separated string
  const ignoredTypes: string[] = [];
  if (fields.IgnoreBarCodeType_UPCA) ignoredTypes.push('UPCA');
  if (fields.IgnoreBarCodeType_UPCE) ignoredTypes.push('UPCE');
  if (fields.IgnoreBarCodeType_EAN2) ignoredTypes.push('EAN2');
  if (fields.IgnoreBarCodeType_EAN5) ignoredTypes.push('EAN5');
  if (fields.IgnoreBarCodeType_EAN8) ignoredTypes.push('EAN8');
  if (fields.IgnoreBarCodeType_EAN13) ignoredTypes.push('EAN13');
  if (fields.IgnoreBarCodeType_I25) ignoredTypes.push('I25');
  if (fields.IgnoreBarCodeType_DATABAR) ignoredTypes.push('DATABAR');
  if (fields.IgnoreBarCodeType_DATABAR_EXP) ignoredTypes.push('DATABAR_EXP');
  if (fields.IgnoreBarCodeType_DATABAR_LTD) ignoredTypes.push('DATABAR_LTD');
  if (fields.IgnoreBarCodeType_CODABAR) ignoredTypes.push('CODABAR');
  if (fields.IgnoreBarCodeType_CODE11) ignoredTypes.push('CODE11');
  if (fields.IgnoreBarCodeType_CODE32) ignoredTypes.push('CODE32');
  if (fields.IgnoreBarCodeType_CODE39) ignoredTypes.push('CODE39');
  if (fields.IgnoreBarCodeType_CODE93) ignoredTypes.push('CODE93');
  if (fields.IgnoreBarCodeType_CODE128) ignoredTypes.push('CODE128');
  if (fields.IgnoreBarCodeType_MSI) ignoredTypes.push('MSI');
  if (fields.IgnoreBarCodeType_TELEPEN) ignoredTypes.push('TELEPEN');
  if (fields.IgnoreBarCodeType_PHARMACODE) ignoredTypes.push('PHARMACODE');
  if (fields.IgnoreBarCodeType_USPS_4STATE) ignoredTypes.push('USPS_4STATE');

  // XML Structure
  lines.push('<?xml version="1.0" encoding="UTF-8"?>');
  lines.push('        <BarcodeParams>');

  // <General> Section
  lines.push('            <General>');
  lines.push(
    `                <RedLightSimulation>${boolToXml(fields.General_RedLightSimulation)}</RedLightSimulation>`,
  );
  lines.push(`                <SmoothFilter>${fields.General_SmoothFilter.trim()}</SmoothFilter>`);
  lines.push(`                <MedianFilter>${fields.General_MedianFilter.trim()}</MedianFilter>`);
  lines.push(`                <OpenFilter>${fields.General_OpenFilter.trim()}</OpenFilter>`);
  lines.push(`                <CloseFilter>${fields.General_CloseFilter.trim()}</CloseFilter>`);
  lines.push('                <DPI>300</DPI>');
  lines.push(`                <OutputFormatGS1>${fields.General_OutputFormatGS1.trim()}</OutputFormatGS1>`);
  lines.push(`                <OutputFormatGrade>${fields.General_OutputFormatGrade.trim()}</OutputFormatGrade>`);
  lines.push('            </General>');

  // <Linear> Section
  lines.push('            <Linear>');
  lines.push(`                <IgnoreBarCode>${boolToXml(fields.Linear_IgnoreBarCode)}</IgnoreBarCode>`);
  lines.push(`                <ISO_IEC_15416_2016>${boolToXml(fields.Linear_ISO_IEC_15416_2016)}</ISO_IEC_15416_2016>`);
  lines.push('                <AutoFiltering>True</AutoFiltering>');
  lines.push(`                <EnableRotation>${boolToXml(fields.Linear_EnableRotation)}</EnableRotation>`);
  lines.push(`                <BackgroundColor>${fields.Linear_BackgroundColor ? '1' : '0'}</BackgroundColor>`);
  if (ignoredTypes.length > 0) {
    lines.push(`                <IgnoreBarCodeType>${ignoredTypes.join(',')}</IgnoreBarCodeType>`);
  }
  lines.push(`                <ScanGapHor>${fields.Linear_ScanGapHor.trim()}</ScanGapHor>`);
  lines.push(`                <ScanGapVer>${fields.Linear_ScanGapVer.trim()}</ScanGapVer>`);
  lines.push(`                <MinScore>${fields.Linear_MinScore.trim()}</MinScore>`);
  lines.push(`                <MinVicinityHor>${fields.Linear_MinVicinityHor.trim()}</MinVicinityHor>`);
  lines.push(`                <MinVicinityVer>${fields.Linear_MinVicinityVer.trim()}</MinVicinityVer>`);
  lines.push(`                <IgnoreChecksum>${boolToXml(fields.Linear_IgnoreChecksum)}</IgnoreChecksum>`);
  lines.push('                <IgnoreQuietZone>False</IgnoreQuietZone>');
  lines.push(`                <GradeNarrowBar>${boolToXml(fields.Linear_GradeNarrowBar)}</GradeNarrowBar>`);
  lines.push(`                <GradeDataStructure>${boolToXml(fields.Linear_GradeDataStructure)}</GradeDataStructure>`);
  lines.push(
    `                <GradeWideToNarrowRatio>${boolToXml(fields.Linear_GradeWideToNarrowRatio)}</GradeWideToNarrowRatio>`,
  );
  lines.push(`                <GradeQuietZone>${boolToXml(fields.Linear_GradeQuietZone)}</GradeQuietZone>`);
  lines.push(`                <FailOutOfRangeBWR>${boolToXml(fields.Linear_FailOutOfRangeBWR)}</FailOutOfRangeBWR>`);
  lines.push('                <NarrowBarNominal>True</NarrowBarNominal>');
  lines.push(
    `                <UspsMeasurementNominal>${boolToXml(fields.Linear_UspsMeasurementNominal)}</UspsMeasurementNominal>`,
  );
  lines.push(`                <EdgeThresh>${fields.Linear_EdgeThresh.trim()}</EdgeThresh>`);
  lines.push(`                <MinPharmacodeWidth>${fields.Linear_MinPharmacodeWidth.trim()}</MinPharmacodeWidth>`);
  lines.push(`                <MaxPharmacodeWidth>${fields.Linear_MaxPharmacodeWidth.trim()}</MaxPharmacodeWidth>`);
  lines.push(`                <MinPharmacodeHeight>${fields.Linear_MinPharmacodeHeight.trim()}</MinPharmacodeHeight>`);
  lines.push(`                <MaxPharmacodeHeight>${fields.Linear_MaxPharmacodeHeight.trim()}</MaxPharmacodeHeight>`);
  lines.push(`                <MinPharmacodeBars>${fields.Linear_MinPharmacodeBars.trim()}</MinPharmacodeBars>`);
  lines.push(`                <MinBWR>${fields.Linear_MinBWR.trim()}</MinBWR>`);
  lines.push(`                <MaxBWR>${fields.Linear_MaxBWR.trim()}</MaxBWR>`);
  lines.push(`                <HumanReadable>${boolToXml(fields.Linear_HumanReadable)}</HumanReadable>`);
  lines.push('            </Linear>');

  // <DataMatrix> Section
  lines.push('            <DataMatrix>');
  lines.push(`                <IgnoreDmtxCode>${boolToXml(fields.DataMatrix_IgnoreDmtxCode)}</IgnoreDmtxCode>`);
  lines.push(
    `                <GradeNonISOParams>${boolToXml(fields.DataMatrix_GradeNonISOParams)}</GradeNonISOParams>`,
  );
  lines.push(`                <EnableMirrored>${boolToXml(fields.DataMatrix_EnableMirrored)}</EnableMirrored>`);
  lines.push(`                <MaxExpectedSymbols>${fields.DataMatrix_MaxExpectedSymbols.trim()}</MaxExpectedSymbols>`);
  lines.push(`                <ScanGap>${fields.DataMatrix_ScanGap.trim()}</ScanGap>`);
  lines.push(`                <LocatorEffortLevel>${fields.DataMatrix_LocatorEffortLevel.trim()}</LocatorEffortLevel>`);
  lines.push(`                <MinModuleSize>${fields.DataMatrix_MinModuleSize.trim()}</MinModuleSize>`);
  lines.push(`                <MaxSymbolSize>${fields.DataMatrix_MaxSymbolSize.trim()}</MaxSymbolSize>`);
  lines.push(`                <MaxSkewAngle>${fields.DataMatrix_MaxSkewAngle.trim()}</MaxSkewAngle>`);
  lines.push(`                <EdgeThresh>${fields.DataMatrix_EdgeThresh.trim()}</EdgeThresh>`);
  lines.push('            </DataMatrix>');

  // <QR> Section
  lines.push('            <QR>');
  lines.push(`                <IgnoreQrCode>${boolToXml(fields.QR_IgnoreQrCode)}</IgnoreQrCode>`);
  lines.push(`                <GradeNonISOParams>${boolToXml(fields.QR_GradeNonISOParams)}</GradeNonISOParams>`);
  lines.push(`                <EnableMirrored>${boolToXml(fields.QR_EnableMirrored)}</EnableMirrored>`);
  lines.push('                <AutoFiltering>True</AutoFiltering>');
  lines.push(`                <ScanGapHor>${fields.QR_ScanGapHor.trim()}</ScanGapHor>`);
  lines.push(`                <ScanGapVer>${fields.QR_ScanGapVer.trim()}</ScanGapVer>`);
  lines.push(`                <MinScore>${fields.QR_MinScore.trim()}</MinScore>`);
  lines.push(`                <EdgeThresh>${fields.QR_EdgeThresh.trim()}</EdgeThresh>`);
  lines.push(`                <ResolvedUrl>${boolToXml(fields.QR_ResolvedUrl)}</ResolvedUrl>`);
  lines.push('            </QR>');

  lines.push('        </BarcodeParams>');
  lines.push('');

  return lines.join('\n');
};

export const parseIniToBarcodeProfileFields = (xmlString: string): BarcodeProfileFields => {
  // Simple XML parser - extracts values between tags
  const getXmlValue = (tag: string, section?: string): string => {
    let searchString = xmlString;
    if (section) {
      // Extract section content first
      const sectionRegex = new RegExp(`<${section}>(.*?)</${section}>`, 's');
      const sectionMatch = xmlString.match(sectionRegex);
      if (sectionMatch) {
        searchString = sectionMatch[1];
      }
    }
    const regex = new RegExp(`<${tag}>(.*?)</${tag}>`);
    const match = searchString.match(regex);
    return match ? match[1].trim() : '';
  };

  const xmlBoolToBool = (value: string): boolean => value?.trim() === 'True';

  // Parse IgnoreBarCodeType comma-separated string into individual checkboxes
  const ignoredTypesString = getXmlValue('IgnoreBarCodeType', 'Linear');
  const ignoredTypesArray = ignoredTypesString ? ignoredTypesString.split(',').map((t) => t.trim()) : [];

  return {
    Linear_IgnoreBarCode: xmlBoolToBool(getXmlValue('IgnoreBarCode', 'Linear')),
    Linear_ScanGapHor: getXmlValue('ScanGapHor', 'Linear') || '2',
    Linear_ScanGapVer: getXmlValue('ScanGapVer', 'Linear') || '2',
    Linear_MinScore: getXmlValue('MinScore', 'Linear') || '5',
    Linear_MinPharmacodeWidth: getXmlValue('MinPharmacodeWidth', 'Linear') || '20',
    Linear_MinPharmacodeHeight: getXmlValue('MinPharmacodeHeight', 'Linear') || '20',
    Linear_MinPharmacodeBars: getXmlValue('MinPharmacodeBars', 'Linear') || '4',
    Linear_MinVicinityHor: getXmlValue('MinVicinityHor', 'Linear') || '64',
    Linear_MinVicinityVer: getXmlValue('MinVicinityVer', 'Linear') || '76',
    Linear_EdgeThresh: getXmlValue('EdgeThresh', 'Linear') || '40',
    Linear_MaxPharmacodeWidth: getXmlValue('MaxPharmacodeWidth', 'Linear') || '600',
    Linear_MaxPharmacodeHeight: getXmlValue('MaxPharmacodeHeight', 'Linear') || '200',
    Linear_ISO_IEC_15416_2016: xmlBoolToBool(getXmlValue('ISO_IEC_15416_2016', 'Linear') || 'True'),
    Linear_EnableRotation: xmlBoolToBool(getXmlValue('EnableRotation', 'Linear') || 'True'),
    Linear_BackgroundColor: (getXmlValue('BackgroundColor', 'Linear') || '0') === '1',
    Linear_IgnoreChecksum: xmlBoolToBool(getXmlValue('IgnoreChecksum', 'Linear')),
    Linear_GradeDataStructure: xmlBoolToBool(getXmlValue('GradeDataStructure', 'Linear')),
    Linear_GradeQuietZone: xmlBoolToBool(getXmlValue('GradeQuietZone', 'Linear') || 'False'),
    Linear_GradeNarrowBar: xmlBoolToBool(getXmlValue('GradeNarrowBar', 'Linear')),
    Linear_GradeWideToNarrowRatio: xmlBoolToBool(getXmlValue('GradeWideToNarrowRatio', 'Linear')),
    Linear_FailOutOfRangeBWR: xmlBoolToBool(getXmlValue('FailOutOfRangeBWR', 'Linear')),
    Linear_MinBWR: getXmlValue('MinBWR', 'Linear') || '-0.1',
    Linear_MaxBWR: getXmlValue('MaxBWR', 'Linear') || '0.1',
    Linear_UspsMeasurementNominal: xmlBoolToBool(getXmlValue('UspsMeasurementNominal', 'Linear') || 'True'),
    Linear_HumanReadable: xmlBoolToBool(getXmlValue('HumanReadable', 'Linear')),

    IgnoreBarCodeType_UPCA: ignoredTypesArray.includes('UPCA'),
    IgnoreBarCodeType_UPCE: ignoredTypesArray.includes('UPCE'),
    IgnoreBarCodeType_EAN2: ignoredTypesArray.includes('EAN2'),
    IgnoreBarCodeType_EAN5: ignoredTypesArray.includes('EAN5'),
    IgnoreBarCodeType_EAN8: ignoredTypesArray.includes('EAN8'),
    IgnoreBarCodeType_EAN13: ignoredTypesArray.includes('EAN13'),
    IgnoreBarCodeType_I25: ignoredTypesArray.includes('I25'),
    IgnoreBarCodeType_DATABAR: ignoredTypesArray.includes('DATABAR'),
    IgnoreBarCodeType_DATABAR_EXP: ignoredTypesArray.includes('DATABAR_EXP'),
    IgnoreBarCodeType_DATABAR_LTD: ignoredTypesArray.includes('DATABAR_LTD'),
    IgnoreBarCodeType_CODABAR: ignoredTypesArray.includes('CODABAR'),
    IgnoreBarCodeType_CODE11: ignoredTypesArray.includes('CODE11'),
    IgnoreBarCodeType_CODE32: ignoredTypesArray.includes('CODE32'),
    IgnoreBarCodeType_CODE39: ignoredTypesArray.includes('CODE39'),
    IgnoreBarCodeType_CODE93: ignoredTypesArray.includes('CODE93'),
    IgnoreBarCodeType_CODE128: ignoredTypesArray.includes('CODE128'),
    IgnoreBarCodeType_MSI: ignoredTypesArray.includes('MSI'),
    IgnoreBarCodeType_TELEPEN: ignoredTypesArray.includes('TELEPEN'),
    IgnoreBarCodeType_PHARMACODE: ignoredTypesArray.includes('PHARMACODE'),
    IgnoreBarCodeType_USPS_4STATE: ignoredTypesArray.includes('USPS_4STATE'),

    DataMatrix_IgnoreDmtxCode: xmlBoolToBool(getXmlValue('IgnoreDmtxCode', 'DataMatrix')),
    DataMatrix_MaxExpectedSymbols: getXmlValue('MaxExpectedSymbols', 'DataMatrix') || '3',
    DataMatrix_ScanGap: getXmlValue('ScanGap', 'DataMatrix') || '8',
    DataMatrix_LocatorEffortLevel: getXmlValue('LocatorEffortLevel', 'DataMatrix') || '1',
    DataMatrix_EdgeThresh: getXmlValue('EdgeThresh', 'DataMatrix') || '10',
    DataMatrix_MinModuleSize: getXmlValue('MinModuleSize', 'DataMatrix') || '4',
    DataMatrix_MaxSymbolSize: getXmlValue('MaxSymbolSize', 'DataMatrix') || '600',
    DataMatrix_MaxSkewAngle: getXmlValue('MaxSkewAngle', 'DataMatrix') || '15',
    DataMatrix_GradeNonISOParams: xmlBoolToBool(getXmlValue('GradeNonISOParams', 'DataMatrix')),
    DataMatrix_EnableMirrored: xmlBoolToBool(getXmlValue('EnableMirrored', 'DataMatrix') || 'True'),

    QR_IgnoreQrCode: xmlBoolToBool(getXmlValue('IgnoreQrCode', 'QR')),
    QR_ScanGapHor: getXmlValue('ScanGapHor', 'QR') || '1',
    QR_ScanGapVer: getXmlValue('ScanGapVer', 'QR') || '1',
    QR_MinScore: getXmlValue('MinScore', 'QR') || '5',
    QR_EdgeThresh: getXmlValue('EdgeThresh', 'QR') || '40',
    QR_GradeNonISOParams: xmlBoolToBool(getXmlValue('GradeNonISOParams', 'QR')),
    QR_EnableMirrored: xmlBoolToBool(getXmlValue('EnableMirrored', 'QR') || 'True'),
    QR_ResolvedUrl: xmlBoolToBool(getXmlValue('ResolvedUrl', 'QR')),

    General_RedLightSimulation: xmlBoolToBool(getXmlValue('RedLightSimulation', 'General') || 'True'),
    General_SmoothFilter: getXmlValue('SmoothFilter', 'General') || '0',
    General_MedianFilter: getXmlValue('MedianFilter', 'General') || '0',
    General_OpenFilter: getXmlValue('OpenFilter', 'General') || '0',
    General_CloseFilter: getXmlValue('CloseFilter', 'General') || '0',
    General_OutputFormatGS1: getXmlValue('OutputFormatGS1', 'General') || '0',
    General_OutputFormatGrade: getXmlValue('OutputFormatGrade', 'General') || '0',
  };
};

const clampNum = (value: string, min: number, max: number, fallback: string): string => {
  if (value.trim() === '') return fallback;
  const num = Number(value);
  if (isNaN(num)) return fallback;
  return String(Math.min(Math.max(num, min), max));
};

const clampMinOnly = (value: string, min: number, fallback: string): string => {
  if (value.trim() === '') return fallback;
  const num = Number(value);
  if (isNaN(num)) return fallback;
  return String(Math.max(num, min));
};

export const clampGraphicProfileFields = (fields: ProfileFields): ProfileFields => ({
  ...fields,
  DiffGrouping: clampNum(fields.DiffGrouping, 0, 999, '50'),
  MatchPer: clampNum(fields.MatchPer, 5, 99, '35'),
  MatchVar: clampNum(fields.MatchVar, 5, 99, '85'),
  CleanEdgePixel: clampNum(fields.CleanEdgePixel, -125, 125, '0'),
  MaskDielinePixel: clampNum(fields.MaskDielinePixel, 0, 125, '0'),

  ContentText_MinDiffDia: clampMinOnly(fields.ContentText_MinDiffDia, 0, '1'),
  ContentText_MaxDiffShift: clampMinOnly(fields.ContentText_MaxDiffShift, 0, '0'),
  ContentText_Sensitivity: clampNum(fields.ContentText_Sensitivity, 1, 254, '25'),
  ContentText_Smoothing: clampMinOnly(fields.ContentText_Smoothing, 0, '1'),
  ContentText_Refine: clampMinOnly(fields.ContentText_Refine, 0, '0'),

  ArtworkText_MinDiffDia: clampMinOnly(fields.ArtworkText_MinDiffDia, 0, '1'),
  ArtworkText_MaxDiffShift: clampMinOnly(fields.ArtworkText_MaxDiffShift, 0, '0'),
  ArtworkText_Sensitivity: clampNum(fields.ArtworkText_Sensitivity, 1, 254, '160'),
  ArtworkText_Smoothing: clampMinOnly(fields.ArtworkText_Smoothing, 0, '5'),
  ArtworkText_Refine: clampMinOnly(fields.ArtworkText_Refine, 0, '5'),

  Content_MinDiffDia: clampMinOnly(fields.Content_MinDiffDia, 0, '10'),
  Content_MaxDiffShift: clampMinOnly(fields.Content_MaxDiffShift, 0, '0'),
  Content_Sensitivity: clampNum(fields.Content_Sensitivity, 1, 254, '40'),
  Content_Smoothing: clampMinOnly(fields.Content_Smoothing, 0, '2'),
  Content_Refine: clampMinOnly(fields.Content_Refine, 0, '2'),

  Artwork_MinDiffDia: clampMinOnly(fields.Artwork_MinDiffDia, 0, '10'),
  Artwork_MaxDiffShift: clampMinOnly(fields.Artwork_MaxDiffShift, 0, '0'),
  Artwork_Sensitivity: clampNum(fields.Artwork_Sensitivity, 1, 254, '40'),
  Artwork_Smoothing: clampMinOnly(fields.Artwork_Smoothing, 0, '2'),
  Artwork_Refine: clampMinOnly(fields.Artwork_Refine, 0, '2'),

  Color_Min: clampMinOnly(fields.Color_Min, 0, '1000'),
  Color_Mean: clampNum(fields.Color_Mean, 0, 225, '120'),
});

export const clampBarcodeProfileFields = (fields: BarcodeProfileFields): BarcodeProfileFields => ({
  ...fields,
  Linear_ScanGapHor: clampNum(fields.Linear_ScanGapHor, 0, 32, '2'),
  Linear_ScanGapVer: clampNum(fields.Linear_ScanGapVer, 0, 32, '2'),
  Linear_MinScore: clampNum(fields.Linear_MinScore, 0, 100, '5'),
  Linear_MinPharmacodeWidth: clampNum(fields.Linear_MinPharmacodeWidth, 4, 1000, '20'),
  Linear_MinPharmacodeHeight: clampNum(fields.Linear_MinPharmacodeHeight, 4, 1000, '20'),
  Linear_MinPharmacodeBars: clampNum(fields.Linear_MinPharmacodeBars, 2, 16, '4'),
  Linear_MinVicinityHor: clampNum(fields.Linear_MinVicinityHor, 1, 1000, '64'),
  Linear_MinVicinityVer: clampNum(fields.Linear_MinVicinityVer, 1, 1000, '76'),
  Linear_EdgeThresh: clampNum(fields.Linear_EdgeThresh, 1, 100, '40'),
  Linear_MaxPharmacodeWidth: clampNum(fields.Linear_MaxPharmacodeWidth, 4, 1000, '600'),
  Linear_MaxPharmacodeHeight: clampNum(fields.Linear_MaxPharmacodeHeight, 4, 1000, '200'),
  Linear_MinBWR: clampNum(fields.Linear_MinBWR, -1, 1, '-0.1'),
  Linear_MaxBWR: clampNum(fields.Linear_MaxBWR, -1, 1, '0.1'),

  DataMatrix_MaxExpectedSymbols: clampNum(fields.DataMatrix_MaxExpectedSymbols, 1, 100, '3'),
  DataMatrix_ScanGap: clampNum(fields.DataMatrix_ScanGap, 0, 32, '8'),
  DataMatrix_LocatorEffortLevel: clampNum(fields.DataMatrix_LocatorEffortLevel, 0, 10, '1'),
  DataMatrix_EdgeThresh: clampNum(fields.DataMatrix_EdgeThresh, 1, 100, '10'),
  DataMatrix_MinModuleSize: clampNum(fields.DataMatrix_MinModuleSize, 1, 100, '4'),
  DataMatrix_MaxSymbolSize: clampNum(fields.DataMatrix_MaxSymbolSize, 1, 2000, '600'),
  DataMatrix_MaxSkewAngle: clampNum(fields.DataMatrix_MaxSkewAngle, 0, 90, '15'),

  QR_ScanGapHor: clampNum(fields.QR_ScanGapHor, 0, 32, '1'),
  QR_ScanGapVer: clampNum(fields.QR_ScanGapVer, 0, 32, '1'),
  QR_MinScore: clampNum(fields.QR_MinScore, 0, 100, '5'),
  QR_EdgeThresh: clampNum(fields.QR_EdgeThresh, 1, 100, '40'),

  General_SmoothFilter: clampNum(fields.General_SmoothFilter, 0, 6, '0'),
  General_MedianFilter: clampNum(fields.General_MedianFilter, 0, 6, '0'),
  General_OpenFilter: clampNum(fields.General_OpenFilter, 0, 6, '0'),
  General_CloseFilter: clampNum(fields.General_CloseFilter, 0, 6, '0'),
  General_OutputFormatGS1: clampNum(fields.General_OutputFormatGS1, 0, 2, '0'),
  General_OutputFormatGrade: clampNum(fields.General_OutputFormatGrade, 0, 1, '0'),
});
